/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable react/no-danger */
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect } from "react"
import { Menu, Row, Col } from "@duxton-web/ui"
import {
  EditableFilled,
  MoreInfoFilled,
  TrashCanFilled,
} from "@duxton-web/icons"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router"
import { actionCreator } from "store/store"
import { CATALOGUES, MENUS } from "constants/routes"
import {
  WithMenuHeader,
  WithMenuHeaderInjectedProps,
} from "components/hoc/WithMenuHeader"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { AppState } from "store/store.type"
import DOMPurify from "dompurify"
import { isMartBusinessType } from "utils/misc"
import { MenuGroupDrawer, MenuGroupLinkedStoreDrawer } from "components/drawers"
import { ConfirmModalV2 } from "components/modals"
import { theme } from "styles/theme"
import useFetchAllMenuGroupLinkedStoreList from "hooks/useFetchAllMenuGroupLinkedStoreList"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import useEnableCatalogStoreForMenuGroup from "hooks/useEnableCatalogStoreForMenuGroup"
import { MenuEditor } from "../../menu/detail/MenuEditor"
import { useIsMartMenuGroup } from "../useIsMartMenuGroup"

export const MenuGroupEditor: FC = () => {
  const {
    drawer,
    modal,
    menuGroupDetail: { menuGroupID, businessType },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)
  const { showMenuGroup, showMenuGroupLinkedStore } = drawer
  const { showDeleteMenu } = modal

  const dispatch = useDispatch()
  const history = useHistory()

  const textMap = useTranslationsMapTyped({
    confirmModalTitleMenu: "menu.menuGroup.deleteMenuGroup.menu.title",
    confirmModalTitleCatalogue:
      "menu.menuGroup.deleteMenuGroup.catelogue.title",
    confirmModalDescriptionMenu: "menu.menuGroup.deleteMenuGroup.menu.body",
    confirmModalDescriptionCatalogue:
      "menu.menuGroup.deleteMenuGroup.catelogue.body",
    confirmModalAgreement: "menu.menuGroup.deleteMenuGroup.ack",
    delete: "menu.menuGroup.deleteMenuGroup.delete",
    back: "menu.menuGroup.deleteMenuGroup.back",
  })

  useEffect(() => {
    if (showDeleteMenu) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupDelete,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }
  }, [showDeleteMenu, menuGroupID])

  const handleClickDelete = useCallback(async () => {
    const clickDeleteTimestamp = new Date().valueOf()

    if (!menuGroupID) {
      return
    }
    const t0 = performance.now()
    const resp: any = await dispatch(
      actionCreator.menu.deleteMenuGroup({
        menuGroupID,
        merchantGroupID,
      }),
    )
    const t1 = performance.now()
    let labels = {
      MERCHANT_GROUP_ID: merchantGroupID,
      MENU_GROUP_ID: menuGroupID,
      LOAD_TIME: t1 - t0,
      LOADING_STATUS: "SUCCESS",
      REASON: "",
      ERROR_MSG: "",
      CLICK_DELETE_TIMESTAMP: clickDeleteTimestamp,
      DELETE_SHOWN_TIMESTAMP: null as any,
    }
    if (resp?.type?.endsWith("_FAIL")) {
      labels = {
        ...labels,
        LOADING_STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
    }
    const responseCode = resp?.payload?.status
    if (responseCode) {
      dispatch(
        actionCreator.menu.toggleModal({
          showDeleteMenu: false,
        }),
      )
      history.push(MENUS)
      labels = {
        ...labels,
        DELETE_SHOWN_TIMESTAMP: new Date().valueOf(),
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupDelete,
      action: EventAction.clickDelete,
      labels,
    })
  }, [menuGroupID, dispatch, history, merchantGroupID])

  const handleMenuGroupDrawerClose = useCallback(() => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        showMenuGroup: false,
      }),
    )
  }, [dispatch])

  return (
    <Fragment>
      <MenuEditor />
      <MenuGroupDrawer
        visible={!!showMenuGroup}
        onClose={handleMenuGroupDrawerClose}
      />
      <MenuGroupLinkedStoreDrawer
        visible={!!showMenuGroupLinkedStore}
        onClose={() => {
          dispatch(
            actionCreator.menu.toggleDrawer({
              showMenuGroupLinkedStore: false,
            }),
          )
        }}
      />
      <ConfirmModalV2
        icon={
          <MoreInfoFilled style={{ color: theme.colors.error, fontSize: 32 }} />
        }
        open={!!showDeleteMenu}
        message={
          isMartBusinessType(businessType ?? 0)
            ? textMap.confirmModalTitleCatalogue
            : textMap.confirmModalTitleMenu
        }
        description={
          isMartBusinessType(businessType ?? 0)
            ? textMap.confirmModalDescriptionCatalogue
            : textMap.confirmModalDescriptionMenu
        }
        agreementMessage={textMap.confirmModalAgreement}
        onCancel={() => {
          dispatch(
            actionCreator.menu.toggleModal({
              showDeleteMenu: false,
            }),
          )
        }}
        onOk={() => handleClickDelete()}
        okText={textMap.delete}
        cancelText={textMap.back}
        okButtonProps={{
          danger: true,
        }}
      />
    </Fragment>
  )
}

export const MenuGroupEditorWithHeaderWrapped = ({
  setHeaderTitle,
  setBackLink,
  setMenuButtonOverlay,
  setContent,
  setMenuVisibleChangeFunc,
}: WithMenuHeaderInjectedProps) => {
  const {
    menuGroupDetail: {
      businessType,
      latestPropagateStatus,
      name: menuGroupName,
      menuGroupID,
    },
    merchantGroupID,
    menuGroupLinkedStores,
    menuGroupLinkedStoresIDs,
  } = useSelector((state: AppState) => state.menu)
  const { showGlobalLoading } = useSelector((state: AppState) => state.global)
  const dispatch = useDispatch()
  const fetchAllMenuGroupLinkedStores = useFetchAllMenuGroupLinkedStoreList()
  const enableCatalogStoreForMenuGroup = useEnableCatalogStoreForMenuGroup(
    merchantGroupID,
  )

  const textMap = useTranslationsMapTyped({
    titleMenu: "menu.title",
    titleCatalogue: "catalogue.title",
    editName: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.editNameButton"
      : "menu.menuGroup.editName.forMenu.editNameButton",
    cloneMenu: "menu.menuGroup.cloneMenu",
    viewLinkedStoresSingular: "menu.menuGroup.header.viewLinkedStores.singular",
    viewLinkedStoresPlural: "menu.menuGroup.header.viewLinkedStores.plural",
    delete: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.deleteMenu"
      : "menu.menuGroup.editName.forMenu.deleteMenu",
    xStores: "menu.xStores",
  })

  const isMartMenuGroup = useIsMartMenuGroup()

  const renderContent = () => {
    const handleClickShowLinkedStores = () => {
      dispatch(
        actionCreator.menu.toggleDrawer({
          showMenuGroupLinkedStore: true,
        }),
      )
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupDetail,
        action: EventAction.clickStoresLinked,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }

    const linkedStoresCount = enableCatalogStoreForMenuGroup
      ? menuGroupLinkedStoresIDs?.length
      : menuGroupLinkedStores?.length

    const content =
      linkedStoresCount !== undefined ? (
        <div
          css={css`
            margin-top: 10px;
            font-size: 18px;
            cursor: pointer;
            display: inline-block;
          `}
          onClick={handleClickShowLinkedStores}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                (linkedStoresCount !== 1
                  ? textMap.viewLinkedStoresPlural
                  : textMap.viewLinkedStoresSingular
                ).replace("<x>", `${linkedStoresCount}`),
              ),
            }}
          />
        </div>
      ) : null

    setContent(content)
  }

  // Update header
  useEffect(() => {
    if (menuGroupName) {
      setHeaderTitle(menuGroupName)
    } else if (showGlobalLoading) {
      setHeaderTitle("")
    } else {
      setHeaderTitle(
        isMartMenuGroup ? textMap.titleCatalogue : textMap.titleMenu,
      )
    }

    const backLink = isMartMenuGroup ? CATALOGUES : MENUS

    setBackLink(backLink)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    menuGroupName,
    isMartMenuGroup,
    textMap.titleCatalogue,
    textMap.titleMenu,
    showGlobalLoading,
  ])

  const handleMenuClick = useCallback(
    ({ key }: any) => {
      let action: EventAction = EventAction.clickEditName
      switch (key) {
        case "edit":
          dispatch(
            actionCreator.menu.toggleDrawer({
              showMenuGroup: true,
            }),
          )
          TroyWebTracker.sendEvent({
            category: EventCategory.menuGroupDetail,
            action: EventAction.pageView,
            labels: {
              MERCHANT_GROUP_ID: merchantGroupID,
              MENU_GROUP_ID: menuGroupID,
            },
          })
          break
        case "delete":
          dispatch(
            actionCreator.menu.toggleModal({
              showDeleteMenu: true,
            }),
          )
          action = EventAction.clickDelete
          break
        default:
          break
      }
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupDetail,
        action,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuGroupID],
  )

  const menuVisibleCallback = useCallback(
    (visible: boolean) => {
      if (visible) {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuGroupDetail,
          action: EventAction.clickMenuTitleMore,
          labels: {
            MERCHANT_GROUP_ID: merchantGroupID,
            MENU_GROUP_ID: menuGroupID,
          },
        })
      }
    },
    [menuGroupID],
  )

  const allowDeleteMenuGroup =
    latestPropagateStatus === 1 && (menuGroupID ?? "").includes("MENU")

  // Update menu overlay button
  useEffect(() => {
    const menuButtonOverlay = (
      <Menu
        css={css`
          min-width: 270px;
          .menu-button-overlay-item-container {
            padding: 18px !important;
          }
          .menu-button-overlay-icon-container {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            margin-right: 12px;
          }
          .menu-button-overlay-icon-container,
          .menu-button-overlay-label-container {
            &.delete {
              color: #f55538;
            }
          }
        `}
        onClick={handleMenuClick}
      >
        <Menu.Item key="edit" className="menu-button-overlay-item-container">
          <Row align="middle">
            <Col className="menu-button-overlay-icon-container">
              <EditableFilled />
            </Col>
            <Col className="menu-button-overlay-label-container">
              {textMap.editName}
            </Col>
          </Row>
        </Menu.Item>
        {allowDeleteMenuGroup && (
          <Menu.Item
            key="delete"
            className="menu-button-overlay-item-container"
          >
            <Row align="middle">
              <Col className="menu-button-overlay-icon-container delete">
                <TrashCanFilled />
              </Col>
              <Col className="menu-button-overlay-label-container delete">
                {textMap.delete}
              </Col>
            </Row>
          </Menu.Item>
        )}
      </Menu>
    )

    setMenuButtonOverlay(menuButtonOverlay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    latestPropagateStatus,
    menuGroupID,
    allowDeleteMenuGroup,
    handleMenuClick,
  ])

  useEffect(() => {
    if (menuGroupID) {
      fetchAllMenuGroupLinkedStores()
    }
  }, [menuGroupID, fetchAllMenuGroupLinkedStores])

  useEffect(renderContent, [
    menuGroupID,
    menuGroupLinkedStores,
    menuGroupLinkedStoresIDs,
    enableCatalogStoreForMenuGroup,
  ])

  useEffect(() => {
    setMenuVisibleChangeFunc(menuVisibleCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuVisibleCallback])

  // unmount
  useEffect(
    () => () => {
      dispatch(actionCreator.menu.clearMenuGroup())
    },
    [],
  )

  return <MenuGroupEditor />
}

export const MenuGroupEditorWithHeader = WithMenuHeader({})(
  MenuGroupEditorWithHeaderWrapped,
)
